import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useWallet } from '../../context/WalletContext';
import { useNotify } from '../../context/NotificationContext';
import { useContracts } from '../../hooks/useContract';
import { parseError } from '../../utils/errors';
import { DEFAULT_REFERRER_ID } from '../../config/contracts';
import MiniLoader from '../common/MiniLoader';
import Card from '../common/Card';

const RegisterForm = ({ onRegistered }) => {
  const { account, connected, connect } = useWallet();
  const { logic, storage } = useContracts();
  const notify = useNotify();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const initialRef = params.get('ref') || String(DEFAULT_REFERRER_ID);
  const [refId, setRefId] = useState(initialRef);
  const [submitting, setSubmitting] = useState(false);

  // Upline info — live lookup as user types the referrer ID
  const [upline, setUpline] = useState(null); // { id, topPkg } | 'notfound' | null
  const [uplineLoading, setUplineLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const lookup = async () => {
      const num = parseInt(refId, 10);
      if (!num || num < 1) { setUpline(null); return; }
      setUplineLoading(true);
      try {
        const u = await storage.users(num);
        if (cancelled) return;
        const id = u[1].toNumber ? u[1].toNumber() : Number(u[1]);
        if (id === 0) {
          setUpline('notfound');
        } else {
          setUpline({
            id,
            topPkg: u[8].toNumber ? u[8].toNumber() : Number(u[8])
          });
        }
      } catch (_) {
        if (!cancelled) setUpline('notfound');
      } finally {
        if (!cancelled) setUplineLoading(false);
      }
    };
    const t = setTimeout(lookup, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [refId, storage]);

  const submit = async (e) => {
    e.preventDefault();
    if (!connected) { connect(); return; }

    const refNum = parseInt(refId, 10);
    if (!refNum || refNum < 1) { notify.error('Invalid referrer ID'); return; }

    if (upline === 'notfound') {
      notify.error('Referrer not found. Please enter a valid referrer ID.');
      return;
    }

    setSubmitting(true);

    try {
      const txLoading = notify.loading('Confirm transaction in wallet...');
      const tx = await logic.register(refNum);
      notify.dismiss(txLoading);
      const mining = notify.loading('Registering... please wait');
      await tx.wait();
      notify.dismiss(mining);
      notify.success('Registration successful! Welcome to Dreamlifeway');

      if (onRegistered) onRegistered();
      navigate('/');
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card title="Create Your Account" subtitle="Enter your referrer to join Dreamlifeway" className="card-glow">
      <form onSubmit={submit}>
        <div className="input-group">
          <label className="input-label">Your Wallet</label>
          <input
            className="input font-mono"
            value={account || 'Not connected'}
            disabled
            readOnly
          />
        </div>

        <div className="input-group">
          <label className="input-label">Referrer ID</label>
          <input
            className="input"
            type="number"
            value={refId}
            onChange={(e) => setRefId(e.target.value)}
            placeholder={`Default: ${DEFAULT_REFERRER_ID}`}
            min="1"
          />

          {/* Upline preview */}
          {uplineLoading && (
            <span className="input-hint">
              <MiniLoader /> Looking up referrer...
            </span>
          )}
          {!uplineLoading && upline && upline !== 'notfound' && (
            <div className="upline-preview">
              <div className="upline-avatar">
                {String(upline.id).charAt(0)}
              </div>
              <div className="upline-info">
                <div className="upline-name">User #{upline.id}</div>
                <div className="upline-meta">
                  ID {upline.id} · Rank P{upline.topPkg || 0}
                </div>
              </div>
              <div className="upline-badge">✓ Verified</div>
            </div>
          )}
          {!uplineLoading && upline === 'notfound' && (
            <span className="input-error">✕ Referrer ID not found</span>
          )}
          {!uplineLoading && !upline && (
            <span className="input-hint">
              {initialRef !== String(DEFAULT_REFERRER_ID) && params.get('ref')
                ? 'Referrer from your invite link'
                : 'Enter a referrer ID or use default'}
            </span>
          )}
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-full btn-lg mt-md"
          disabled={submitting || !connected || upline === 'notfound'}
        >
          {submitting ? <><MiniLoader /> Processing</> : (connected ? 'Register Now' : 'Connect Wallet First')}
        </button>
      </form>
    </Card>
  );
};

export default RegisterForm;
